import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { AuthContext } from "../AuthProvider";
import RealTimeSMSService from "./services/RealTimeSMSService";
import {
  FaSms,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaClock,
  FaEnvelope,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { collection, query, getDocs, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";

const RealTimeSMS = ({ selectedTeam }) => {
  const { user } = useContext(AuthContext);

  // UI state
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState("");

  // SMS state
  const [smsMessage, setSmsMessage] = useState("");
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [senderEmail, setSenderEmail] = useState(user?.email || "");

  // Custom message toggle
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [customMessage, setCustomMessage] = useState("");

  // Sending state
  const [sending, setSending] = useState(false);
  const [sentMessages, setSentMessages] = useState([]);

  // Load prospects
  useEffect(() => {
    loadProspects();
  }, []);

  // Set default email
  useEffect(() => {
    if (user && user.email) {
      setSenderEmail(user.email);
    }
  }, [user]);

  const loadProspects = async () => {
    setLoadingProspects(true);
    try {
      const contactsRef = collection(db, "contacts");
      const q = query(contactsRef, orderBy("fullName"), limit(100));
      const snapshot = await getDocs(q);

      const prospectData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setProspects(prospectData);
    } catch (error) {
      console.error("Error loading prospects:", error);
      toast.error("Failed to load prospects");
    } finally {
      setLoadingProspects(false);
    }
  };

  const generateSMSMessage = async () => {
    if (useCustomMessage) {
      // Use custom message directly
      if (!customMessage.trim()) {
        toast.error("Please enter a custom SMS message");
        return;
      }

      // Add email signature
      const messageWithEmail = `${customMessage.trim()}\n\nReply to this email: ${senderEmail}`;
      setSmsMessage(messageWithEmail);
      toast.success("Custom SMS message ready!");
      return;
    }

    // Generate from email using AI
    if (!selectedProspect || !selectedEmail) {
      toast.error("Please select a prospect and paste an email");
      return;
    }

    setGeneratingMessage(true);
    try {
      const result = await RealTimeSMSService.generateSMSFromEmail({
        emailSample: selectedEmail,
        contactName: selectedProspect.fullName || selectedProspect.name,
        contactTitle: selectedProspect.title,
        companyName: selectedProspect.companyName || selectedProspect.company,
        contactEmail: selectedProspect.email,
        sellerName: user.displayName || user.email,
        sellerEmail: senderEmail,
        sellerRole: "Sales Development Representative",
        sellerCompany: "ReWorld",
        campaignGoal: "Book a meeting",
      });

      setSmsMessage(result.message);
      toast.success("SMS message generated!");
    } catch (error) {
      console.error("Error generating SMS:", error);
      toast.error(error.message || "Failed to generate SMS message");
    } finally {
      setGeneratingMessage(false);
    }
  };

  const sendSMS = async () => {
    const phoneToSend = testPhoneNumber.trim() || selectedProspect?.phone;

    if (!phoneToSend) {
      toast.error(
        "Please enter a phone number or select a prospect with a phone number"
      );
      return;
    }

    if (!smsMessage) {
      toast.error("Please generate an SMS message first");
      return;
    }

    if (!selectedTeam || !selectedTeam.id) {
      toast.error("No team selected. Please select a team from the dropdown.");
      return;
    }

    setSending(true);

    try {
      const result = await RealTimeSMSService.sendSMS({
        teamId: selectedTeam.id,
        prospectId: selectedProspect?.id || "test_prospect",
        phoneNumber: phoneToSend,
        message: smsMessage,
        prospectName:
          selectedProspect?.fullName ||
          selectedProspect?.name ||
          "Test Contact",
        prospectEmail: selectedProspect?.email,
        prospectCompany:
          selectedProspect?.companyName || selectedProspect?.company,
      });

      toast.success("SMS sent successfully!");

      // Add to sent messages list
      setSentMessages((prev) => [
        {
          id: result.messageSid,
          phone: phoneToSend,
          message: smsMessage,
          status: "sent",
          timestamp: new Date().toISOString(),
        },
        ...prev,
      ]);

      // Clear form
      setSmsMessage("");
      setCustomMessage("");
      setSelectedEmail("");
    } catch (error) {
      console.error("SMS send failed:", error);
      toast.error(error.message || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  const getCharacterCount = () => {
    const count = smsMessage.length;
    const segments = Math.ceil(count / 160);
    return { count, segments };
  };

  const { count, segments } = smsMessage
    ? getCharacterCount()
    : { count: 0, segments: 0 };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FaSms className="mr-3 text-blue-600" />
            Real-Time SMS Outreach
          </h1>
          <p className="text-gray-600 mt-1">
            Send AI-generated or custom SMS messages to prospects
          </p>
        </div>
      </div>

      {/* Prospect Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          1. Select Prospect
        </h2>

        {loadingProspects ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-blue-600 text-2xl" />
          </div>
        ) : (
          <select
            value={selectedProspect?.id || ""}
            onChange={(e) => {
              const prospect = prospects.find((p) => p.id === e.target.value);
              setSelectedProspect(prospect);
              setSmsMessage("");
            }}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a prospect...</option>
            {prospects.map((prospect) => (
              <option key={prospect.id} value={prospect.id}>
                {prospect.fullName || prospect.name}
                {prospect.companyName ? ` - ${prospect.companyName}` : ""}
                {prospect.phone ? ` (${prospect.phone})` : " (No phone)"}
              </option>
            ))}
          </select>
        )}

        {selectedProspect && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-semibold text-gray-700">Name:</span>
                <p className="text-gray-900">
                  {selectedProspect.fullName || selectedProspect.name}
                </p>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Phone:</span>
                <p className="text-gray-900">
                  {selectedProspect.phone || "Not set"}
                </p>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Company:</span>
                <p className="text-gray-900">
                  {selectedProspect.companyName ||
                    selectedProspect.company ||
                    "N/A"}
                </p>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Email:</span>
                <p className="text-gray-900">
                  {selectedProspect.email || "N/A"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Test Phone Number Override */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Test Phone Number (Override)
            <span className="ml-2 text-xs text-gray-500 font-normal">
              Optional - for testing, not saved
            </span>
          </label>
          <input
            type="tel"
            value={testPhoneNumber}
            onChange={(e) => setTestPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter a phone number to test SMS sending. This will override the
            prospect's phone number and won't be saved.
          </p>
        </div>

        {/* Sender Email */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Email (will be included in SMS)
            <FaEnvelope className="inline ml-2 text-blue-600" />
          </label>
          <input
            type="email"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            placeholder="your.email@reworld.com"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Your email will be automatically added to the SMS message so
            prospects can reply
          </p>
        </div>
      </div>

      {/* Message Generation */}
      {selectedProspect && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            2. Create SMS Message
          </h2>

          {/* Toggle between AI and Custom */}
          <div className="mb-4 flex items-center space-x-4">
            <button
              onClick={() => {
                setUseCustomMessage(false);
                setSmsMessage("");
              }}
              className={`px-4 py-2 rounded-md font-medium ${
                !useCustomMessage
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              AI Generated
            </button>
            <button
              onClick={() => {
                setUseCustomMessage(true);
                setSmsMessage("");
              }}
              className={`px-4 py-2 rounded-md font-medium ${
                useCustomMessage
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Custom Message
            </button>
          </div>

          {!useCustomMessage ? (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference Email (paste email you sent)
              </label>
              <textarea
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                rows={6}
                placeholder="Paste the email you sent to this prospect..."
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ) : (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom SMS Message
                <span className="ml-2 text-xs text-gray-500 font-normal">
                  (Keep under 160 characters for single SMS)
                </span>
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={4}
                maxLength={300}
                placeholder="Hi John, following up on my email about waste management solutions..."
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>
                  Note: Your email will be automatically added at the end
                </span>
                <span>{customMessage.length}/300 characters</span>
              </div>
            </div>
          )}

          <button
            onClick={generateSMSMessage}
            disabled={
              generatingMessage ||
              (!useCustomMessage && !selectedEmail) ||
              (useCustomMessage && !customMessage.trim())
            }
            className={`w-full inline-flex justify-center items-center px-6 py-3 rounded-md font-medium ${
              generatingMessage ||
              (!useCustomMessage && !selectedEmail) ||
              (useCustomMessage && !customMessage.trim())
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {generatingMessage ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Generating Message...
              </>
            ) : useCustomMessage ? (
              <>Prepare Custom Message</>
            ) : (
              <>Generate SMS Message</>
            )}
          </button>

          {smsMessage && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-green-800">
                  SMS Message Preview:
                </p>
                <span className="text-xs text-gray-600">
                  {count} chars | {segments} SMS{" "}
                  {segments > 1 ? "segments" : "segment"}
                </span>
              </div>
              <p className="text-gray-800 whitespace-pre-line text-sm">
                {smsMessage}
              </p>
              {segments > 1 && (
                <p className="mt-2 text-xs text-orange-600">
                  ⚠️ This message will be sent as {segments} SMS segments (costs
                  ${segments * 0.0079} per send)
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Send SMS */}
      {smsMessage && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            3. Send SMS
          </h2>

          {testPhoneNumber && (
            <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">Test Mode:</span> Will send to{" "}
                {testPhoneNumber} instead of prospect's number
              </p>
            </div>
          )}

          <button
            onClick={sendSMS}
            disabled={sending || (!testPhoneNumber && !selectedProspect?.phone)}
            className={`w-full px-6 py-4 rounded-md font-medium text-lg flex items-center justify-center ${
              sending || (!testPhoneNumber && !selectedProspect?.phone)
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            {sending ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Sending SMS...
              </>
            ) : (
              <>
                <FaSms className="mr-2" />
                Send SMS to{" "}
                {testPhoneNumber ||
                  (selectedProspect?.phone
                    ? `${selectedProspect.fullName || selectedProspect.name}`
                    : "Test Number")}
              </>
            )}
          </button>

          {!testPhoneNumber && !selectedProspect?.phone && (
            <p className="mt-2 text-sm text-red-600 text-center">
              Please enter a test phone number or select a prospect with a phone
              number
            </p>
          )}
        </div>
      )}

      {/* Sent Messages History */}
      {sentMessages.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Sent Messages
          </h2>
          <div className="space-y-3">
            {sentMessages.map((msg, idx) => (
              <div
                key={idx}
                className="p-3 bg-gray-50 border border-gray-200 rounded-md"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {msg.phone}
                  </span>
                  <span className="flex items-center text-xs text-green-600">
                    <FaCheck className="mr-1" /> Sent
                  </span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line">
                  {msg.message.substring(0, 100)}
                  {msg.message.length > 100 ? "..." : ""}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(msg.timestamp).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          How It Works
        </h3>
        <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
          <li>Select a prospect from your contacts (or use test mode)</li>
          <li>Enter your email address (will be included in SMS)</li>
          <li>Choose AI Generated or Custom Message mode</li>
          <li>For AI: Paste the email you sent and generate SMS summary</li>
          <li>For Custom: Type your own message directly</li>
          <li>Click "Send SMS" to deliver via Twilio</li>
          <li>Message is tracked in Firestore and sent messages list</li>
        </ol>
        <div className="mt-3 pt-3 border-t border-gray-300">
          <p className="text-xs text-gray-600">
            <span className="font-semibold">Pricing:</span> $0.0079 per SMS
            segment. Messages over 160 characters cost multiple segments.
          </p>
          <p className="text-xs text-gray-600 mt-1">
            <span className="font-semibold">Best Practice:</span> Keep messages
            concise (under 160 chars) for single-segment delivery and better
            response rates.
          </p>
        </div>
      </div>
    </div>
  );
};

RealTimeSMS.propTypes = {
  selectedTeam: PropTypes.shape({
    id: PropTypes.string.isRequired,
    teamName: PropTypes.string,
  }),
};

export default RealTimeSMS;
